class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');

    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    if (FacetFiltersForm.historyListenerInitialized) return;

    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);

    FacetFiltersForm.historyListenerInitialized = true;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoadingState(isLoading) {
    const productGridContainer =
      document.getElementById('ProductGridContainer');

    const collection =
      productGridContainer?.querySelector('.collection');

    if (collection) {
      collection.classList.toggle('loading', isLoading);
    }

    const countContainer =
      document.getElementById('ProductCount');

    const countContainerDesktop =
      document.getElementById('ProductCountDesktop');

    if (countContainer) {
      countContainer.classList.toggle('loading', isLoading);
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.toggle(
        'loading',
        isLoading
      );
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => {
      spinner.classList.toggle('hidden', !isLoading);
    });
  }

  static renderPage(
    searchParams,
    event,
    updateURLHash = true
  ) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();

    if (!sections.length) {
      console.warn(
        '[Facets] No product-grid section was found.'
      );

      FacetFiltersForm.setLoadingState(false);
      return;
    }

    /*
     * Abort any previous AJAX filter request.
     *
     * This prevents an older, slower response from
     * overwriting a newer filter selection.
     */
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }

    FacetFiltersForm.abortController =
      new AbortController();

    const signal =
      FacetFiltersForm.abortController.signal;

    const requestId =
      ++FacetFiltersForm.renderRequestId;

    FacetFiltersForm.setLoadingState(true);

    const renderTasks = sections.map((section) => {
      const url =
        `${window.location.pathname}` +
        `?section_id=${encodeURIComponent(section.section)}` +
        `${searchParams ? `&${searchParams}` : ''}`;

      const cachedItem =
        FacetFiltersForm.filterData.find(
          (item) => item.url === url
        );

      if (cachedItem) {
        try {
          FacetFiltersForm.renderSectionFromCache(
            cachedItem,
            event
          );

          return Promise.resolve();
        } catch (error) {
          return Promise.reject(error);
        }
      }

      return FacetFiltersForm.renderSectionFromFetch(
        url,
        event,
        signal
      );
    });

    Promise.allSettled(renderTasks).then((results) => {
      /*
       * Ignore completion from an older request.
       */
      if (
        requestId !==
        FacetFiltersForm.renderRequestId
      ) {
        return;
      }

      results.forEach((result) => {
        if (
          result.status === 'rejected' &&
          result.reason?.name !== 'AbortError'
        ) {
          console.error(
            '[Facets] Failed to update filters:',
            result.reason
          );
        }
      });

      FacetFiltersForm.setLoadingState(false);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(
    url,
    event,
    signal
  ) {
    return fetch(url, {
      method: 'GET',
      signal,
      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Facet request failed: HTTP ${response.status}`
          );
        }

        return response.text();
      })
      .then((responseText) => {
        if (signal.aborted) return;

        /*
         * Replace any existing cache entry for
         * the same URL.
         */
        FacetFiltersForm.filterData =
          FacetFiltersForm.filterData.filter(
            (item) => item.url !== url
          );

        FacetFiltersForm.filterData.push({
          html: responseText,
          url,
        });

        /*
         * Prevent an unlimited in-memory cache.
         */
        if (
          FacetFiltersForm.filterData.length >
          FacetFiltersForm.maxCacheEntries
        ) {
          FacetFiltersForm.filterData.shift();
        }

        FacetFiltersForm.renderSection(
          responseText,
          event
        );
      });
  }

  static renderSectionFromCache(
    filterData,
    event
  ) {
    if (!filterData?.html) return;

    FacetFiltersForm.renderSection(
      filterData.html,
      event
    );
  }

  static renderSection(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    FacetFiltersForm.renderFilters(
      parsedHTML,
      event
    );

    FacetFiltersForm.renderProductGridContainer(
      parsedHTML
    );

    FacetFiltersForm.renderProductCount(
      parsedHTML
    );

    FacetFiltersForm.reinitializeScrollAnimations();
  }

  static reinitializeScrollAnimations() {
    if (
      typeof initializeScrollAnimationTrigger !==
      'function'
    ) {
      return;
    }

    const productGrid =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!productGrid) return;

    try {
      /*
       * Passing the actual DOM element fixes the
       * previous html.innerHTML issue.
       *
       * If the theme implementation does not require
       * an argument, JavaScript safely ignores it.
       */
      initializeScrollAnimationTrigger(
        productGrid
      );
    } catch (error) {
      console.warn(
        '[Facets] Scroll animation reinitialization failed:',
        error
      );
    }
  }

  static renderProductGridContainer(parsedHTML) {
    const source =
      parsedHTML.getElementById(
        'ProductGridContainer'
      );

    const target =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!source || !target) {
      throw new Error(
        'ProductGridContainer was not found in the AJAX response.'
      );
    }

    target.innerHTML = source.innerHTML;

    target
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add(
          'scroll-trigger--cancel'
        );
      });
  }

  static renderProductCount(parsedHTML) {
    const container =
      document.getElementById(
        'ProductCount'
      );

    const containerDesktop =
      document.getElementById(
        'ProductCountDesktop'
      );

    const desktopSource =
      parsedHTML.getElementById(
        'ProductCountDesktop'
      );

    const mobileSource =
      parsedHTML.getElementById(
        'ProductCount'
      );

    let count = '';

    if (desktopSource) {
      count = desktopSource.innerHTML;
    }

    if (mobileSource) {
      count = mobileSource.innerHTML;
    }

    if (container) {
      container.innerHTML = count;
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, ' +
          '#FacetFiltersFormMobile .js-filter, ' +
          '#FacetFiltersPillsForm .js-filter'
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, ' +
          '#FacetFiltersFormMobile .js-filter, ' +
          '#FacetFiltersPillsForm .js-filter'
      );

    /*
     * Remove filters that are no longer returned
     * by Shopify.
     */
    Array.from(
      facetDetailsElementsFromDom
    ).forEach((currentElement) => {
      const stillExists = Array.from(
        facetDetailsElementsFromFetch
      ).some(
        ({ id }) =>
          currentElement.id === id
      );

      if (!stillExists) {
        currentElement.remove();
      }
    });

    const eventTarget =
      event?.target instanceof Element
        ? event.target
        : null;

    const activeJSFilter =
      eventTarget?.closest('.js-filter');

    const matchesId = (element) => {
      return activeJSFilter
        ? element.id === activeJSFilter.id
        : false;
    };

    const facetsToRender = Array.from(
      facetDetailsElementsFromFetch
    ).filter(
      (element) => !matchesId(element)
    );

    const countsToRender = Array.from(
      facetDetailsElementsFromFetch
    ).find(matchesId);

    facetsToRender.forEach(
      (elementToRender, index) => {
        const currentElement =
          document.getElementById(
            elementToRender.id
          );

        if (currentElement) {
          currentElement.innerHTML =
            elementToRender.innerHTML;

          return;
        }

        if (index > 0) {
          const previousElement =
            facetsToRender[index - 1];

          if (
            previousElement &&
            elementToRender.className ===
              previousElement.className
          ) {
            const previousDOMElement =
              document.getElementById(
                previousElement.id
              );

            if (previousDOMElement) {
              previousDOMElement.after(
                elementToRender
              );

              return;
            }
          }
        }

        const parentId =
          elementToRender.parentElement?.id;

        if (!parentId) return;

        const parent =
          document.getElementById(parentId);

        if (!parent) return;

        const firstFilter =
          parent.querySelector('.js-filter');

        if (firstFilter) {
          firstFilter.before(
            elementToRender
          );
        } else {
          parent.appendChild(
            elementToRender
          );
        }
      }
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML
    );

    if (
      countsToRender &&
      activeJSFilter
    ) {
      const closestJSFilterID =
        activeJSFilter.id;

      if (closestJSFilterID) {
        const currentTarget =
          document.getElementById(
            closestJSFilterID
          );

        if (currentTarget) {
          FacetFiltersForm.renderCounts(
            countsToRender,
            currentTarget
          );

          FacetFiltersForm.renderMobileCounts(
            countsToRender,
            currentTarget
          );
        }

        const newFacetDetailsElement =
          document.getElementById(
            closestJSFilterID
          );

        if (!newFacetDetailsElement) return;

        const newElementSelector =
          newFacetDetailsElement.classList.contains(
            'mobile-facets__details'
          )
            ? '.mobile-facets__close-button'
            : '.facets__summary';

        const newElementToActivate =
          newFacetDetailsElement.querySelector(
            newElementSelector
          );

        const isTextInput =
          eventTarget?.getAttribute('type') ===
          'text';

        if (
          newElementToActivate &&
          !isTextInput
        ) {
          newElementToActivate.focus();
        }
      }
    }
  }

  static renderActiveFacets(parsedHTML) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const source =
          parsedHTML.querySelector(selector);

        const target =
          document.querySelector(selector);

        if (!source || !target) return;

        target.innerHTML =
          source.innerHTML;
      }
    );

    FacetFiltersForm.toggleActiveFacets(
      false
    );
  }

  static renderAdditionalElements(parsedHTML) {
    const selectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    selectors.forEach((selector) => {
      const source =
        parsedHTML.querySelector(selector);

      const target =
        document.querySelector(selector);

      if (!source || !target) return;

      target.innerHTML =
        source.innerHTML;
    });

    const menuDrawer =
      document
        .getElementById(
          'FacetFiltersFormMobile'
        )
        ?.closest('menu-drawer');

    if (
      menuDrawer &&
      typeof menuDrawer.bindEvents ===
        'function'
    ) {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary =
      target.querySelector(
        '.facets__summary'
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary'
      );

    if (
      sourceSummary &&
      targetSummary
    ) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header'
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header'
      );

    if (
      sourceHeaderElement &&
      targetHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector(
        '.facets-wrap'
      );

    const sourceWrapElement =
      source.querySelector(
        '.facets-wrap'
      );

    if (
      sourceWrapElement &&
      targetWrapElement
    ) {
      const isShowingMore = Boolean(
        target.querySelector(
          'show-more-button .label-show-more.hidden'
        )
      );

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(
            '.facets__item.hidden'
          )
          .forEach((hiddenItem) => {
            hiddenItem.classList.replace(
              'hidden',
              'show-more-item'
            );
          });
      }

      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;

    const targetFacetsList =
      target.querySelector(
        '.mobile-facets__list'
      );

    const sourceFacetsList =
      source.querySelector(
        '.mobile-facets__list'
      );

    if (
      sourceFacetsList &&
      targetFacetsList
    ) {
      targetFacetsList.outerHTML =
        sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url =
      `${window.location.pathname}` +
      `${
        searchParams
          ? `?${searchParams}`
          : ''
      }`;

    history.pushState(
      { searchParams },
      '',
      url
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    const sectionId =
      productGrid?.dataset?.id;

    if (!sectionId) return [];

    return [
      {
        section: sectionId,
      },
    ];
  }

  createSearchParams(form) {
    if (!form) return '';

    const formData = new FormData(form);

    return new URLSearchParams(
      formData
    ).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(
      searchParams,
      event
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sourceForm =
      event.target?.closest?.('form');

    if (!sourceForm) return;

    const allFacetForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    const isMobile = [
      'FacetFiltersFormMobile',
      'FacetSortDrawerForm',
    ].includes(sourceForm.id);

    const allowedFormIds = isMobile
      ? [
          'FacetFiltersFormMobile',
          'FacetSortDrawerForm',
        ]
      : [
          'FacetFiltersForm',
          'FacetSortForm',
        ];

    /*
     * Merge the two related forms while preventing
     * duplicate identical parameters such as q or
     * filter.v.availability.
     *
     * Different values of the same filter are kept,
     * which is required for multi-select filters.
     */
    const mergedParams =
      new URLSearchParams();

    const seenPairs = new Set();

    allFacetForms.forEach((form) => {
      if (
        !allowedFormIds.includes(form.id)
      ) {
        return;
      }

      const formData =
        new FormData(form);

      for (const [key, value] of formData.entries()) {
        if (
          typeof value !== 'string'
        ) {
          continue;
        }

        const pairKey =
          `${key}\u0000${value}`;

        if (seenPairs.has(pairKey)) {
          continue;
        }

        seenPairs.add(pairKey);

        mergedParams.append(
          key,
          value
        );
      }
    });

    this.onSubmitForm(
      mergedParams.toString(),
      event
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const href =
      event.currentTarget?.href;

    if (!href) return;

    let searchParams = '';

    try {
      const url = new URL(
        href,
        window.location.origin
      );

      searchParams =
        url.search.slice(1);
    } catch (error) {
      console.warn(
        '[Facets] Invalid filter URL:',
        href
      );

      return;
    }

    FacetFiltersForm.renderPage(
      searchParams
    );
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

FacetFiltersForm.abortController = null;

FacetFiltersForm.renderRequestId = 0;

FacetFiltersForm.maxCacheEntries = 30;

FacetFiltersForm.historyListenerInitialized =
  false;

if (
  !customElements.get(
    'facet-filters-form'
  )
) {
  customElements.define(
    'facet-filters-form',
    FacetFiltersForm
  );

  FacetFiltersForm.setListeners();
}


class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach(
      (element) => {
        element.addEventListener(
          'change',
          this.onRangeChange.bind(this)
        );

        element.addEventListener(
          'keydown',
          this.onKeyDown.bind(this)
        );
      }
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(
      event.currentTarget
    );

    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (
      event.metaKey ||
      event.ctrlKey
    ) {
      return;
    }

    const allowedKeys = [
      'Tab',
      'Backspace',
      'Enter',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Escape',
      '.',
      ',',
    ];

    if (
      allowedKeys.includes(event.key)
    ) {
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      return;
    }

    event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    if (inputs.length < 2) return;

    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute(
        'data-max',
        maxInput.value
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'data-min',
        minInput.value
      );
    }

    if (minInput.value === '') {
      maxInput.setAttribute(
        'data-min',
        '0'
      );
    }

    if (maxInput.value === '') {
      const originalMax =
        maxInput.getAttribute(
          'data-max'
        );

      if (originalMax !== null) {
        minInput.setAttribute(
          'data-max',
          originalMax
        );
      }
    }
  }

  adjustToValidValues(input) {
    if (!input) return;

    const value = Number(
      input.value
    );

    const minAttribute =
      input.getAttribute('data-min');

    const maxAttribute =
      input.getAttribute('data-max');

    const min =
      minAttribute !== null
        ? Number(minAttribute)
        : null;

    const max =
      maxAttribute !== null
        ? Number(maxAttribute)
        : null;

    if (
      Number.isFinite(min) &&
      value < min
    ) {
      input.value = min;
    }

    if (
      Number.isFinite(max) &&
      value > max
    ) {
      input.value = max;
    }
  }
}

if (
  !customElements.get('price-range')
) {
  customElements.define(
    'price-range',
    PriceRange
  );
}


class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute(
      'role',
      'button'
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this)
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          event.preventDefault();

          this.closeFilter(event);
        }
      }
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form'
      ) ||
      document.querySelector(
        'facet-filters-form'
      );

    if (
      !form ||
      typeof form.onActiveFilterClick !==
        'function'
    ) {
      return;
    }

    form.onActiveFilterClick(event);
  }
}

if (
  !customElements.get('facet-remove')
) {
  customElements.define(
    'facet-remove',
    FacetRemove
  );
}